/**
 * モザイク処理ユーティリティ
 *
 * model_utils.pyのmosaic_area関数のロジックをCanvas APIで実装
 *
 * 処理フロー:
 * 1. 長辺リサイズ（long_side_resize）でモザイクサイズを一定に
 * 2. 縮小→拡大でモザイク処理
 * 3. 元サイズに戻す
 * 4. 赤色マスク部分を検出
 * 5. マスク部分のみモザイク画像を適用
 */

export interface MosaicOptions {
  /** モザイクの粗さ（0.01〜0.2程度、小さいほど粗い）デフォルト: 0.2（元のPython設定） */
  mosaicRatio?: number;
  /** 長辺の目標サイズ（モザイクブロックサイズを一定にする）デフォルト: 768（元のPython設定） */
  longLen?: number;
  /** 赤色検出の閾値（赤チャンネルの最小値）デフォルト: 200 */
  redThreshold?: number;
  /** 赤色検出の閾値（緑・青チャンネルの最大値）デフォルト: 100 */
  otherThreshold?: number;
}

/**
 * 長辺を指定サイズにリサイズ（アスペクト比維持）
 * model_utils.pyのlong_side_resize関数と同じロジック
 *
 * @param sourceCanvas 元のCanvas
 * @param longLen 長辺の目標サイズ
 * @returns リサイズ後のCanvasと元サイズ情報
 */
function longSideResize(
  sourceCanvas: HTMLCanvasElement,
  longLen: number
): { canvas: HTMLCanvasElement; originalWidth: number; originalHeight: number } {
  const width = sourceCanvas.width;
  const height = sourceCanvas.height;

  // 既に小さければそのまま返す
  if (longLen > Math.max(width, height)) {
    return { canvas: sourceCanvas, originalWidth: width, originalHeight: height };
  }

  // 長辺と短辺を判定
  const isWidthLonger = width > height;
  const longerSide = isWidthLonger ? width : height;
  const shorterSide = isWidthLonger ? height : width;

  // 新しいサイズを計算（アスペクト比維持）
  const newLongerSide = longLen;
  const newShorterSide = Math.floor((longLen / longerSide) * shorterSide);

  const newWidth = isWidthLonger ? newLongerSide : newShorterSide;
  const newHeight = isWidthLonger ? newShorterSide : newLongerSide;

  // リサイズ用Canvas
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = newWidth;
  resizedCanvas.height = newHeight;
  const resizedCtx = resizedCanvas.getContext('2d')!;
  resizedCtx.drawImage(sourceCanvas, 0, 0, newWidth, newHeight);

  return { canvas: resizedCanvas, originalWidth: width, originalHeight: height };
}

/**
 * 画像全体にモザイク処理を適用してCanvasを返す
 * model_utils.pyのmosaic_area関数と同じロジック:
 * 1. long_side_resize で長辺をlongLenにリサイズ
 * 2. mosaicRatio で縮小（INTER_NEAREST）
 * 3. 元サイズに拡大（INTER_NEAREST）
 *
 * @param sourceCanvas 元のCanvas
 * @param mosaicRatio モザイクの粗さ
 * @param longLen 長辺の目標サイズ
 * @returns モザイク処理後のCanvas（元サイズ）
 */
function createMosaicImage(
  sourceCanvas: HTMLCanvasElement,
  mosaicRatio: number,
  longLen: number
): HTMLCanvasElement {
  const originalWidth = sourceCanvas.width;
  const originalHeight = sourceCanvas.height;

  // 1. 長辺リサイズ（モザイクブロックサイズを一定にするため）
  const { canvas: resizedCanvas } = longSideResize(sourceCanvas, longLen);
  const resizedWidth = resizedCanvas.width;
  const resizedHeight = resizedCanvas.height;

  // 2. 縮小サイズを計算（最小4px）
  const smallWidth = Math.max(Math.floor(resizedWidth * mosaicRatio), 4);
  const smallHeight = Math.max(Math.floor(resizedHeight * mosaicRatio), 4);

  // 縮小用Canvas
  const smallCanvas = document.createElement('canvas');
  smallCanvas.width = smallWidth;
  smallCanvas.height = smallHeight;
  const smallCtx = smallCanvas.getContext('2d')!;

  // INTER_NEARESTに相当: imageSmoothingEnabled = false
  smallCtx.imageSmoothingEnabled = false;
  smallCtx.drawImage(resizedCanvas, 0, 0, smallWidth, smallHeight);

  // 3. リサイズ後のサイズに拡大（INTER_NEAREST）
  const mosaicResizedCanvas = document.createElement('canvas');
  mosaicResizedCanvas.width = resizedWidth;
  mosaicResizedCanvas.height = resizedHeight;
  const mosaicResizedCtx = mosaicResizedCanvas.getContext('2d')!;
  mosaicResizedCtx.imageSmoothingEnabled = false;
  mosaicResizedCtx.drawImage(smallCanvas, 0, 0, resizedWidth, resizedHeight);

  // 4. 元のサイズに戻す
  const mosaicCanvas = document.createElement('canvas');
  mosaicCanvas.width = originalWidth;
  mosaicCanvas.height = originalHeight;
  const mosaicCtx = mosaicCanvas.getContext('2d')!;
  mosaicCtx.imageSmoothingEnabled = false;
  mosaicCtx.drawImage(mosaicResizedCanvas, 0, 0, originalWidth, originalHeight);

  return mosaicCanvas;
}

/**
 * 赤色マスクを検出してboolean配列を返す
 * 赤色（RGB: 255, 0, 0付近）で描かれた領域を検出
 */
function extractRedMask(
  imageData: ImageData,
  redThreshold: number,
  otherThreshold: number
): boolean[] {
  const { data, width, height } = imageData;
  const mask: boolean[] = new Array(width * height);

  for (let i = 0; i < width * height; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];

    // 赤色部分を検出（R > threshold, G < threshold, B < threshold）
    mask[i] = r > redThreshold && g < otherThreshold && b < otherThreshold;
  }

  return mask;
}

/**
 * マスク部分にモザイク画像を適用
 * model_utils.pyの img_cv2[y1:y2, x1:x2] = mosaic_img_cv2[y1:y2, x1:x2] に相当
 */
function applyMosaicToMask(
  originalImageData: ImageData,
  mosaicImageData: ImageData,
  mask: boolean[]
): ImageData {
  const result = new ImageData(
    new Uint8ClampedArray(originalImageData.data),
    originalImageData.width,
    originalImageData.height
  );

  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      // マスク部分にモザイク画像のピクセルを適用
      result.data[i * 4] = mosaicImageData.data[i * 4];       // R
      result.data[i * 4 + 1] = mosaicImageData.data[i * 4 + 1]; // G
      result.data[i * 4 + 2] = mosaicImageData.data[i * 4 + 2]; // B
      result.data[i * 4 + 3] = mosaicImageData.data[i * 4 + 3]; // A
    }
  }

  return result;
}

/**
 * Konva Stageから赤色マスク付き画像を取得（非同期）
 * toDataURL()を使用してより確実に画像を取得
 */
export async function getStageImageData(stage: any): Promise<{
  canvas: HTMLCanvasElement;
  imageData: ImageData;
}> {
  return new Promise((resolve, reject) => {
    try {
      // StageからDataURLを取得
      const dataUrl = stage.toDataURL({ pixelRatio: 1 });
      console.log('[mosaicUtils] Got dataUrl from stage, length:', dataUrl?.length);

      // DataURLから画像を作成
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // 画像をCanvasに描画
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        console.log('[mosaicUtils] Created imageData:', canvas.width, 'x', canvas.height);
        resolve({ canvas, imageData });
      };
      img.onerror = (err) => {
        console.error('[mosaicUtils] Image load error:', err);
        reject(new Error('Failed to load stage image'));
      };
      img.src = dataUrl;
    } catch (error) {
      console.error('[mosaicUtils] getStageImageData error:', error);
      reject(error);
    }
  });
}

/**
 * 元画像（マスクなし）を読み込んでCanvasを返す
 */
export async function loadImageToCanvas(imageUrl: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // blob URLやdata URLの場合はcrossOriginを設定しない（same-originなので不要）
    if (!imageUrl.startsWith('blob:') && !imageUrl.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    };
    img.onerror = (e) => {
      console.error('[mosaicUtils] loadImageToCanvas error:', e, 'url:', imageUrl);
      reject(e);
    };
    img.src = imageUrl;
  });
}

/**
 * マスク画像（Konva Stage）と元画像URLからモザイク処理を適用
 *
 * @param stage Konva Stage（赤色マスクが描かれた状態）
 * @param originalImageUrl 元画像のURL
 * @param options モザイクオプション
 * @returns モザイク処理された画像のBlob
 */
export async function applyMosaicToMaskedArea(
  stage: any,
  originalImageUrl: string,
  options: MosaicOptions = {}
): Promise<Blob> {
  const {
    mosaicRatio = 0.2,
    longLen = 768,
    redThreshold = 200,
    otherThreshold = 100,
  } = options;

  // 1. Konva Stageからマスク付き画像を取得（表示サイズ）
  const { canvas: maskedCanvas, imageData: maskedImageData } = await getStageImageData(stage);
  const stageWidth = maskedCanvas.width;
  const stageHeight = maskedCanvas.height;

  // 2. 元画像を読み込み（元のサイズを維持）
  console.log('[mosaicUtils] Loading original image, type:', originalImageUrl?.substring(0, 20));
  const originalCanvas = await loadImageToCanvas(originalImageUrl);
  const originalWidth = originalCanvas.width;
  const originalHeight = originalCanvas.height;
  console.log('[mosaicUtils] Original image loaded:', originalWidth, 'x', originalHeight);
  console.log('[mosaicUtils] Stage size:', stageWidth, 'x', stageHeight);

  // 3. マスクを元画像サイズにスケールアップ（元画像サイズを維持するため）
  const scaledMaskCanvas = document.createElement('canvas');
  scaledMaskCanvas.width = originalWidth;
  scaledMaskCanvas.height = originalHeight;
  const scaledMaskCtx = scaledMaskCanvas.getContext('2d')!;
  // マスクの境界を保持するためにスムージングを無効化
  scaledMaskCtx.imageSmoothingEnabled = false;
  scaledMaskCtx.drawImage(maskedCanvas, 0, 0, originalWidth, originalHeight);
  const scaledMaskedImageData = scaledMaskCtx.getImageData(0, 0, originalWidth, originalHeight);

  // 4. 元画像全体にモザイク処理（長辺リサイズ→縮小→拡大→元サイズに戻す）
  const mosaicCanvas = createMosaicImage(originalCanvas, mosaicRatio, longLen);
  const mosaicCtx = mosaicCanvas.getContext('2d')!;
  const mosaicImageData = mosaicCtx.getImageData(0, 0, originalWidth, originalHeight);

  // 5. スケールアップしたマスクから赤色マスクを検出
  const mask = extractRedMask(scaledMaskedImageData, redThreshold, otherThreshold);
  const redPixelCount = mask.filter(Boolean).length;
  console.log('[mosaicUtils] Red mask detected:', redPixelCount, 'pixels out of', mask.length);

  // 6. マスク部分にモザイク画像を適用（元画像サイズで処理）
  const originalCtx = originalCanvas.getContext('2d')!;
  const originalImageData = originalCtx.getImageData(0, 0, originalWidth, originalHeight);
  const resultImageData = applyMosaicToMask(originalImageData, mosaicImageData, mask);

  // 7. 結果をCanvasに描画（元画像サイズを維持）
  const resultCanvas = document.createElement('canvas');
  resultCanvas.width = originalWidth;
  resultCanvas.height = originalHeight;
  const resultCtx = resultCanvas.getContext('2d')!;
  resultCtx.putImageData(resultImageData, 0, 0);

  console.log('[mosaicUtils] Output size:', originalWidth, 'x', originalHeight);

  // 8. BlobとしてHigh Quality JPEGで出力
  return new Promise((resolve, reject) => {
    resultCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/jpeg',
      0.92
    );
  });
}

/**
 * BlobからDataURLを生成
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * モザイク処理のプレビュー用（リアルタイム表示）
 * Canvasを直接返す
 */
export async function createMosaicPreview(
  stage: any,
  originalImageUrl: string,
  options: MosaicOptions = {}
): Promise<HTMLCanvasElement> {
  const {
    mosaicRatio = 0.2,
    longLen = 768,
    redThreshold = 200,
    otherThreshold = 100,
  } = options;

  const { canvas: maskedCanvas, imageData: maskedImageData } = await getStageImageData(stage);
  const width = maskedCanvas.width;
  const height = maskedCanvas.height;

  const originalCanvas = await loadImageToCanvas(originalImageUrl);

  const resizedOriginalCanvas = document.createElement('canvas');
  resizedOriginalCanvas.width = width;
  resizedOriginalCanvas.height = height;
  const resizedCtx = resizedOriginalCanvas.getContext('2d')!;
  resizedCtx.drawImage(originalCanvas, 0, 0, width, height);

  const mosaicCanvas = createMosaicImage(resizedOriginalCanvas, mosaicRatio, longLen);
  const mosaicCtx = mosaicCanvas.getContext('2d')!;
  const mosaicImageData = mosaicCtx.getImageData(0, 0, width, height);

  const mask = extractRedMask(maskedImageData, redThreshold, otherThreshold);

  const originalImageData = resizedCtx.getImageData(0, 0, width, height);
  const resultImageData = applyMosaicToMask(originalImageData, mosaicImageData, mask);

  const resultCanvas = document.createElement('canvas');
  resultCanvas.width = width;
  resultCanvas.height = height;
  const resultCtx = resultCanvas.getContext('2d')!;
  resultCtx.putImageData(resultImageData, 0, 0);

  return resultCanvas;
}
